import { useEffect, useRef, useCallback, useMemo } from 'react';

/**
 * @param {Object} options
 * @param {number} options.appHeaderHeight
 * @param {boolean} options.enabled
 * @param {string} options.transformProperty
 * @param {number} options.throttleMs
 * @param {React.RefObject} options.scrolledContainerRef - Ref to the scrollable parent container
 * @returns {Object} - { tableRef, theadRef }
 */

/**
 * Removes overflow:auto or overflow-x:auto from ancestor elements
 * — but only when there is no active horizontal scroll on the page.
 * Returns a cleanup function that restores original styles.
 *
 * @param {HTMLElement} element
 * @returns {() => void} cleanup function
 */

// Helper function to detect if there's any active horizontal scroll on the page
const hasActiveHorizontalScroll = () => {
  return Array.from(document.querySelectorAll('*')).some((el) => {
    const style = window.getComputedStyle(el);
    // Skip elements that can't scroll horizontally
    const canScroll =
      (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
      el.scrollWidth > el.clientWidth;
    return canScroll;
  });
};

let _overflowState = []; // store changes globally between calls

function toggleAutoOverflowClasses(shouldDisable) {
  const element = document.getElementsByTagName('table')?.[0];
  if (!element) return;

  // If shouldDisable = true → remove & override overflow
  if (shouldDisable) {
    if (_overflowState.length === 0) {
      _overflowState = [];
    } // reset previous state

    let current = element.parentElement;
    while (current && current !== document.body && current.tagName !== 'MAIN') {
      const computed = window.getComputedStyle(current);
      const before = {
        element: current,
        hadOverflowXAutoClass: current.classList.contains('overflow-x-auto'),
        hadOverflowHiddenClass: current.classList.contains('overflow-hidden'),
        hadOverflowAutoClass: current.classList.contains('overflow-auto'),
        hadOverflowScrollClass: current.classList.contains('overflow-scroll'),
        previousOverflow: current.style.overflow,
        previousOverflowX: current.style.overflowX,
        previousOverflowY: current.style.overflowY,
        // Store computed values to restore CSS class-based styles
        computedOverflow: computed.overflow,
        computedOverflowX: computed.overflowX,
        computedOverflowY: computed.overflowY,
      };

      // Remove Tailwind / utility classes
      if (before.hadOverflowXAutoClass) {
        current.classList.remove('overflow-x-auto');
      }
      if (before.hadOverflowHiddenClass) {
        current.classList.remove('overflow-hidden');
      }
      if (before.hadOverflowAutoClass) {
        current.classList.remove('overflow-auto');
      }
      if (before.hadOverflowScrollClass) {
        current.classList.remove('overflow-scroll');
      }

      // Force override inline styles
      if (
        computed.overflowX === 'auto' ||
        computed.overflowX === 'scroll' ||
        computed.overflow === 'hidden' ||
        computed.overflow === 'auto' ||
        computed.overflow === 'scroll'
      ) {
        current.style.setProperty('overflow', 'visible', 'important');
        current.style.setProperty('overflow-x', 'visible', 'important');
        current.style.setProperty('overflow-y', 'visible', 'important');
      }

      _overflowState.push(before);
      current = current.parentElement;
    }

    return;
  }

  // restore previous overflow states
  if (!shouldDisable && _overflowState.length > 0) {
    _overflowState.forEach(
      ({
        element,
        hadOverflowXAutoClass,
        hadOverflowHiddenClass,
        hadOverflowAutoClass,
        hadOverflowScrollClass,
        previousOverflow,
        previousOverflowX,
        previousOverflowY,
        computedOverflow,
        computedOverflowX,
        computedOverflowY,
      }) => {
        // Restore classes if they were removed
        if (hadOverflowXAutoClass) element.classList.add('overflow-x-auto');
        if (hadOverflowHiddenClass) element.classList.add('overflow-hidden');
        if (hadOverflowAutoClass) element.classList.add('overflow-auto');
        if (hadOverflowScrollClass) element.classList.add('overflow-scroll');

        // Restore inline styles - only if they were originally set inline
        if (previousOverflow) {
          element.style.overflow = previousOverflow;
        } else {
          element.style.removeProperty('overflow');
        }
        if (previousOverflowX) {
          element.style.overflowX = previousOverflowX;
        } else {
          element.style.removeProperty('overflow-x');
        }
        if (previousOverflowY) {
          element.style.overflowY = previousOverflowY;
        } else {
          element.style.removeProperty('overflow-y');
        }

        // If the original overflow was set via CSS classes (not inline styles),
        // we need to restore it by setting the inline style to 'auto' instead of 'visible'
        // But only if the element doesn't have the corresponding CSS class
        if (
          !previousOverflow &&
          computedOverflow &&
          computedOverflow !== 'visible' &&
          !element.classList.contains('overflow-auto') &&
          !element.classList.contains('overflow-scroll') &&
          !element.classList.contains('overflow-hidden')
        ) {
          element.style.setProperty('overflow', 'auto', 'important');
        }
        if (
          !previousOverflowX &&
          computedOverflowX &&
          computedOverflowX !== 'visible' &&
          !element.classList.contains('overflow-x-auto') &&
          !element.classList.contains('overflow-auto') &&
          !element.classList.contains('overflow-scroll')
        ) {
          element.style.setProperty('overflow-x', 'auto', 'important');
        }
        if (
          !previousOverflowY &&
          computedOverflowY &&
          computedOverflowY !== 'visible' &&
          !element.classList.contains('overflow-y-auto') &&
          !element.classList.contains('overflow-auto') &&
          !element.classList.contains('overflow-scroll')
        ) {
          element.style.setProperty('overflow-y', 'auto', 'important');
        }
      }
    );

    // Don't clear _overflowState here - we need to preserve it for future restorations
    // _overflowState = []; // clear after restore
  }
}

export const useStickyHeader = ({
  appHeaderHeight = 64,
  enabled = true,
  transformProperty = 'translateY',
  throttleMs = 16, // 60fps
  scrolledContainerRef = null,
} = {}) => {
  const tableRef = useRef(null);
  const theadRef = useRef(null);
  const rafIdRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const tableTopRef = useRef(0);
  const isStickyRef = useRef(false);

  const mainScroll = document.getElementsByTagName('main')[0];

  const throttle = useCallback((func, delay) => {
    let timeoutId;
    let lastExecTime = 0;

    return (...args) => {
      const currentTime = Date.now();

      if (currentTime - lastExecTime > delay) {
        func(...args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func(...args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }, []);

  const calculateTransform = useCallback(
    (scrollY, tableTop, isContainerScroll = false) => {
      let shouldBeSticky;
      let transformValue;

      if (isContainerScroll) {
        // For container scroll, table should stick when it reaches the top of the container
        // tableTop represents the position of the table relative to the container
        shouldBeSticky = scrollY >= tableTop;
        transformValue = shouldBeSticky ? scrollY - tableTop : 0;
      } else {
        // For window scroll, use the original logic
        const stickyThreshold = tableTop - appHeaderHeight;

        shouldBeSticky = scrollY >= stickyThreshold;
        transformValue = shouldBeSticky ? scrollY - stickyThreshold : 0;
      }

      if (shouldBeSticky && !isStickyRef.current) {
        isStickyRef.current = true;
      } else if (!shouldBeSticky && isStickyRef.current) {
        isStickyRef.current = false;
      }

      return transformValue;
    },
    [appHeaderHeight]
  );

  const handleScroll = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const thead = theadRef.current;
      const table = tableRef.current;

      if (!thead || !table) return;

      // Get scroll position from container or window
      const scrollY = scrolledContainerRef?.current
        ? scrolledContainerRef.current.scrollTop
        : mainScroll.scrollTop;

      if (Math.abs(scrollY - lastScrollYRef.current) > 50) {
        if (scrolledContainerRef?.current) {
          // For container scroll, get table position relative to container
          const containerRect =
            scrolledContainerRef.current.getBoundingClientRect();
          const tableRect = table.getBoundingClientRect();
          // Calculate the table's position relative to the container's content
          tableTopRef.current = tableRect.top - containerRect.top;
        } else {
          // For window scroll, use existing calculation
          tableTopRef.current = table.getBoundingClientRect().top + scrollY;
        }
      }

      lastScrollYRef.current = scrollY;

      const isContainerScroll = !!scrolledContainerRef?.current;
      const hasHorizontalScroll = hasActiveHorizontalScroll();

      if (hasHorizontalScroll) {
        toggleAutoOverflowClasses(false);
        // If there's horizontal scroll, use translateY transform
        const transformValue = calculateTransform(
          scrollY,
          tableTopRef.current,
          isContainerScroll
        );

        const currentTransform = thead.style.transform;
        const newTransform = `${transformProperty}(${transformValue}px)`;

        if (currentTransform !== newTransform) {
          thead.style.transform = newTransform;
          thead.style.zIndex = '99';
        }

        // Reset sticky positioning
        thead.style.position = '';
        thead.style.top = '';
        thead.style.zIndex = '99';
      } else {
        // If no horizontal scroll, temporarily remove overflow classes and use sticky positioning
        toggleAutoOverflowClasses(true);

        thead.style.position = 'sticky';
        thead.style.zIndex = '99';
        thead.style.top = `0px`;
        thead.style.transform = '';
      }
    });
  }, [transformProperty, calculateTransform, scrolledContainerRef, mainScroll]);

  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, throttleMs),
    [handleScroll, throttle, throttleMs]
  );

  const handleResize = useCallback(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleScroll, scrolledContainerRef]);

  const throttledHandleResize = useMemo(
    () => throttle(handleResize, 100),
    [handleResize, throttle]
  );

  useEffect(() => {
    if (!enabled || !mainScroll) return;

    // Capture the container ref at the beginning of the effect
    const container = scrolledContainerRef?.current;

    // Simple retry mechanism
    const initializeStickyHeader = () => {
      const thead = theadRef.current;
      const table = tableRef.current;

      if (!thead || !table) {
        // Retry after a short delay
        setTimeout(initializeStickyHeader, 50);
        return;
      }

      // Initialize position based on scroll source
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const tableRect = table.getBoundingClientRect();
        const scrollY = container.scrollTop;
        tableTopRef.current = tableRect.top - containerRect.top;
        lastScrollYRef.current = scrollY;
      } else {
        tableTopRef.current =
          table.getBoundingClientRect().top + mainScroll.scrollTop;
        lastScrollYRef.current = mainScroll.scrollTop;
      }

      isStickyRef.current = false;

      // Add event listeners to appropriate target
      if (container) {
        container.addEventListener('scroll', throttledHandleScroll, {
          passive: true,
        });
        // For container scroll, we still listen to window resize
        window.addEventListener('resize', throttledHandleResize, {
          passive: true,
        });
      } else {
        mainScroll.addEventListener('scroll', throttledHandleScroll, {
          passive: true,
        });
        window.addEventListener('resize', throttledHandleResize, {
          passive: true,
        });
      }

      handleScroll();
    };

    // Start initialization
    initializeStickyHeader();

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Restore all overflow states when component unmounts
      toggleAutoOverflowClasses(false);

      // Clear the overflow state after final restoration
      _overflowState = [];

      // Remove event listeners from appropriate target
      if (container) {
        container.removeEventListener('scroll', throttledHandleScroll);
        window.removeEventListener('resize', throttledHandleResize);
      } else {
        window.removeEventListener('scroll', throttledHandleScroll);
        window.removeEventListener('resize', throttledHandleResize);
      }
    };
  }, [
    enabled,
    throttledHandleScroll,
    throttledHandleResize,
    handleScroll,
    scrolledContainerRef,
    mainScroll,
  ]);

  return {
    tableRef,
    theadRef,
  };
};
